import { configureStore, combineReducers } from '@reduxjs/toolkit';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import authReducer from './slices/authSlice';
import taskReducer from './slices/taskSlice';
import { apiSlice } from './api/apiSlice';

const combinedReducer = combineReducers({
  auth: authReducer,
  task: taskReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
});

type CombinedState = {
  auth: ReturnType<typeof authReducer>;
  task: ReturnType<typeof taskReducer>;
  [apiSlice.reducerPath]: ReturnType<typeof apiSlice.reducer>;
};

/**
 * Root reducer wrapper
 *
 * When the authenticated identity changes (login, register, logout) the entire
 * RTK Query cache is wiped so that tenant A's data is NEVER visible to tenant B.
 */
const rootReducer = (state: CombinedState | undefined, action: { type: string }): CombinedState => {
  if (
    action.type === 'auth/logout' ||
    action.type === 'auth/login/fulfilled' ||
    action.type === 'auth/register/fulfilled' ||
    action.type === 'auth/setTokens' // login / SSO — not renewSessionTokens
  ) {
    // Drop the entire API cache – keep the rest of the state so the auth slice
    // can still write the new user/tokens in the same reducer pass.
    const { [apiSlice.reducerPath]: _stale, ...rest } = (state ?? {}) as CombinedState & Record<string, unknown>;
    return combinedReducer(rest as CombinedState, action as any);
  }
  return combinedReducer(state, action as any);
};

/**
 * Redux Store Configuration
 * Combines all reducers and middleware for state management
 */
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(apiSlice.middleware as any),
  devTools: import.meta.env.DEV,
});

// Type exports for TypeScript support
export type RootState = CombinedState;
export type AppDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

// Typed hooks for use throughout the application
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
